/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2020, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { validateURL } from './validators';

export const required =
  ({ message = 'This field is required' } = {}) =>
  value =>
    !value || value.trim().length === 0 ? message : true;

/**
 * Used to test if input is not just all spaces.
 *
 * In other words, we don't want the input to be just spaces. it can be empty
 * `''` and it can have any valid string, `'hi'` or `'hello there'` or even
 * `'     hi'`, but we don't want it to just be spaces, this is invalid: `'  '`,
 * `' '`, etc.
 **/
export const notAllSpaces =
  ({ message = 'This field is required' } = {}) =>
  value =>
    value.length > 0 && value.trim() === '' ? message : true;

export const maxLength =
  ({ length = 100, message = 'Max length of 100' } = {}) =>
  value =>
    value && value.length > length ? message : true;

export const validURL =
  ({ message = 'URL is not valid' } = {}, schemes = []) =>
  value =>
    validateURL(value, schemes) ? message : true;

export const safeChars =
  ({
    regex = /[^\t\n\r]*$/,
    message = 'No carriage returns or tabs permitted'
  } = {}) =>
  value =>
    !value || !value.match(regex) ? message : true;

export const noBlackCircles =
  ({
    regex = /^[^\u25CF]*$/,
    message = "The ● character is not permitted. Click 'Show' to show the secret's characters first."
  } = {}) =>
  value =>
    !value || !value.match(regex) ? message : true;

const COLOR_REGEX = {
  hex: /^#([a-f0-9]{6}|[a-f0-9]{3})\b$/i
};
// We only support these characters for phone numbers...
export const phoneNumberChars =
  ({
    regex = /[^0-9()+\-\s]/,
    message = 'Invalid characters in phone number'
  } = {}) =>
  value =>
    !value || value.match(regex) ? message : true;

// We only support these characters for phone numbers or empty.
export const phoneNumberOrEmpty =
  ({
    regex = /[^0-9()+\-\s]/,
    message = 'Invalid characters in phone number'
  } = {}) =>
  value =>
    value.match(regex) ? message : true;

export const isColorOrEmpty =
  ({
    isValidColor = (color, message) => {
      if (!color || !color.length) {
        return true;
      }
      if (color.length !== 4 && color.length !== 7) {
        return message;
      }
      const keys = Object.keys(COLOR_REGEX);
      for (let i = 0; i < keys.length; i++) {
        const expression = COLOR_REGEX[keys[i]];
        if (expression.test(color)) {
          return true;
        }
      }
      return message;
    },
    message = 'Please enter a proper HEX code based color value.'
  }) =>
  value =>
    isValidColor(value, message);

export const uniqueNameValidation =
  ({ names = [], message = 'Your name must be unique' }) =>
  value =>
    names && names.includes(value) ? message : true;

export const uniqueNameCaseInsensitiveValidation =
  ({ names = [], message = 'Your name must be unique' }) =>
  value => {
    const lower = names
      ? names.map(name => (name ? name.toLowerCase() : name))
      : names;
    return lower && lower.includes(value.toLowerCase()) ? message : true;
  };

export const noSysPrefix =
  ({
    regex = /^(?!sys[_\\-\\.]{1}).*$/,
    message = 'Cannot start with a prefix of "sys"'
  } = {}) =>
  value =>
    !value || !value.match(regex) ? message : true;

export const noSystemPrefix =
  ({
    regex = /^(?!system[_\\-\\.]{1}).*$/,
    message = 'Cannot start with a prefix of "system"'
  } = {}) =>
  value =>
    !value || !value.match(regex) ? message : true;

export const noUserDefinedPrefix =
  ({
    regex = /^(?!user_defined[_\\-\\.]{1}).*$/,
    message = 'Cannot start with a prefix of "user_defined"'
  } = {}) =>
  value =>
    !value || !value.match(regex) ? message : true;

export const validatePort = port => {
  const regex =
    /^([0-9]{1,4}|[1-5][0-9]{4}|6[0-4][0-9]{3}|65[0-4][0-9]{2}|655[0-2][0-9]|6553[0-5])$/;
  return regex.test(port);
};
